<template>
  <div>
    <div v-if="variables && variables.length" class="flex items-center h-12 w-full border-t dark:border-gray-800 px-6">
      <o-dropdown ref="dropdown" class="h-7">
        <o-button size="sm" class="my-auto">
          {{ $t('components.elements.sms.insertPlaceholder') }}
        </o-button>

        <template #content>
          <o-scroll>
            <ul class="max-h-56">
              <li
                v-for="(variable, index) in variables"
                :key="index"
                class="text-gray-900 dark:text-gray-100 cursor-pointer select-none relative py-2 px-3 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                role="option"
                @click="handleVariableClick(variable)"
              >
                {{ variable }}
              </li>
            </ul>
          </o-scroll>
        </template>
      </o-dropdown>
    </div>

    <text-area-field
      v-model="_value"
      :placeholder="placeholder"
      rows="4"
      :base-class="['w-full block bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 text-sm p-3', variables && variables.length ? '' : 'rounded-t']"
    />

    <div class="border border-t-0 rounded-b w-full bg-gray-50 dark:bg-gray-900 dark:border-gray-800 px-2 py-1 flex items-center justify-between">
      <div>
        <o-tooltip v-if="counter && counter.messages > 1" :text="$t('sms.multipleSMSTooltip')">
          <o-chip variant="info" flat size="sm" square>
            {{ $t('sms.multipleSMS') }}
            <o-icon icon="info" class="inline-block ml-3" />
          </o-chip>
        </o-tooltip>
      </div>
      <div class="flex items-center space-x-4">
        <div>
          <o-tooltip :text="$t('sms.specialTooltip')">
            <o-chip v-if="counter && counter.special" variant="warning" flat size="sm" square>
              {{ $t('sms.special', { special: counter.special }) }}
            </o-chip>
          </o-tooltip>
        </div>
        <div class="text-sm font-semibold text-gray-700">
          <o-tooltip>
            <o-chip icon="info">
              <template v-if="counter">
                {{ counter.remaining }} / {{ counter.messages }}
              </template>
              <!-- eslint-disable-next-line vue/no-bare-strings-in-template -->
              <template v-else>
                160 / 0
              </template>
            </o-chip>

            <template #content>
              <p v-html="$t('sms.remainingMessages', { remaining: counter.remaining, messages: counter.messages })" />
            </template>
          </o-tooltip>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
export default { name: 'OSms' }
</script>

<script setup lang="ts">
import {
  TextAreaField,
} from '@/components/fields'

const { $api } = useNuxtApp()

interface Props {
  modelValue?: string | null
  placeholder?: string | null
  variables?: any[]
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: '',
  placeholder: null,
  variables: () => [],
})

const emit = defineEmits<{
  (e: 'update:modelValue', value: string): void
}>()

const dropdown = ref<any>()

const gsm7bitChars = '@£$¥èéùìòÇ\\nØø\\rÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉ !\\"#¤%&\'()*+,-./0123456789:;<=>?¡ABCDEFGHIJKLMNOPQRSTUVWXYZÄÖÑÜ§¿abcdefghijklmnopqrstuvwxyzäöñüà'
const gsm7bitExChar = '\\^{}\\\\\\[~\\]|€'

const article = ref(false)
const counter = ref<any>(null)
const credits = ref<any>(null)
const gsm7bitRegExp = RegExp('^[' + gsm7bitChars + ']*$')
const gsm7bitExRegExp = RegExp('^[' + gsm7bitChars + gsm7bitExChar + ']*$')
const gsm7bitExRegExpRev = RegExp('[' + gsm7bitChars + gsm7bitExChar + ']+', 'g')
const gsm7bitExOnlyRegExp = RegExp('^[\\' + gsm7bitExChar + ']*$')

const _value = computed({
  get () {
    return props.modelValue ?? ''
  },
  set (val: string) {
    emit('update:modelValue', val)
  },
})

function detectEncoding (text: string) {
  switch (false) {
    case text.match(gsm7bitRegExp) == null:
      return 'GSM_7BIT'
    case text.match(gsm7bitExRegExp) == null:
      return 'GSM_7BIT_EX'
    default:
      return 'UTF16'
  }
}

function countGsm7bitEx (text: string) {
  let char2: string
  const chars: string[] = []
  for (let _i = 0, _len = text.length; _i < _len; _i++) {
    char2 = text[_i]
    if (char2.match(gsm7bitExOnlyRegExp) != null) {
      chars.push(char2)
    }
  }
  return chars.length
}

function count () {
  const text = _value.value.trim()
  const encoding = detectEncoding(text)
  let special: string | undefined

  const messageLength: Record<string, number> = {
    GSM_7BIT: 160,
    GSM_7BIT_EX: 160,
    UTF16: 70,
  }

  const multiMessageLength: Record<string, number> = {
    GSM_7BIT: 153,
    GSM_7BIT_EX: 153,
    UTF16: 67,
  }

  let length = text.length
  if (encoding === 'GSM_7BIT_EX') {
    length += countGsm7bitEx(text)
  }

  if (encoding === 'UTF16') {
    const unmatched = text.replace(gsm7bitExRegExpRev, '').replace(/\s/g, '')
    const unique = [...new Set(unmatched)]
    special = unique.join(', ')
  }

  let per_message = messageLength[encoding]
  if (length > per_message) {
    per_message = multiMessageLength[encoding]
  }

  const messages = Math.ceil(length / per_message)

  let remaining = (per_message * messages) - length
  if (remaining === 0 && messages === 0) {
    remaining = per_message
  }

  return {
    encoding,
    length,
    per_message,
    remaining,
    messages,
    special,
  }
}

async function fetchSMSCredits () {
  try {
    const response = await $api.settings.smsCountry()

    if (response?.data) {
      credits.value = response.data
    }
  } catch (e) {
    console.log(e)
  }
}

function handleVariableClick (variable: string) {
  _value.value += ` ${variable} `
  dropdown.value.close()
}

watch(_value, () => {
  counter.value = count()
}, { immediate: true })
</script>
